import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import * as XLSX from 'xlsx';

import { configs } from '../../../../constants/configs/configs';
import { api } from '../../../../modules/common-utils-services/axiosUtil';
import { startSpinner, stopSpinner } from '../../../../actions/common/actionCreators';
import { SPINNER_MESSAGES_IDS } from '../../../../constants/common/miscellaneous';
import { formatDate } from '../../../../utils/dates/date.utils';
import { DD_MM_YYYY } from  '../../../../utils/dates/dateFormats.utils';

interface IDrug {
    rxcui: string;
    name: string;
    strength: string;
    route: string;
    fullName: string;
    rxTermDosageForm: string;
    rxNormDosageForm: string;
    type: string;
    genericName: string | null;
    genericRxcui: string | null;
    status: string;
    remappedTo: string | null;
}

const manageDrugsColumns = [
    {
        id: 'rxcui',
        text: 'RXCUI'
    },
    {
        id: 'name',
        text: 'Name'
    },
    {
        id: 'strength',
        text: 'Strength'
    },
    {
        id: 'route',
        text: 'Route'
    },
    {
        id: 'fullName',
        text: 'Full Name'
    },
    {
        id: 'genericRxcui',
        text: 'Genric RXCUI'
    },
    {
        id: 'status',
        text: 'Status'
    }
];

const MAX_DRUG_INPUT = 20;

export const useManageDrugs = () => {
    const dispatch = useDispatch();
    const [inputDrugs, setInputDrugs] = useState('');
    const [drugList, setDrugList] = useState<IDrug[]>([]);
    const [invalidId, setInvalidId] = useState<null | string>(null);
    const [isInvalidLength, setIsInvalidLength] = useState(false);

    const getDrugsList = async (drugsList: string) => {
        const url = configs.endpoints.ADMIN_GET_DRUG_LIST.processReplacements({ idList: drugsList });
        try {
            dispatch(startSpinner(SPINNER_MESSAGES_IDS.LOADING_DATA)); // Start spinner
            const res = await api.get(url); // This should call the API
            return res;
        } catch (e) {
            console.error('Error while checking for pending application:', e);
            throw new Error(e);
        } finally {
            dispatch(stopSpinner()); // Stop spinner
        }
    };

    const handleDrugIdsChange: React.ChangeEventHandler<HTMLTextAreaElement> = (event) => {
        const inputValue = event.target.value;
        setInputDrugs(inputValue);

        // Split the input string by commas to check the number of IDs
        const drugIds = inputValue.split(',').map(id => id.trim());

        // Find all invalid IDs
        const invalidParts = drugIds.filter(id => !/^\d+$/.test(id));

        // Check if the number of comma-separated IDs is less than 20
        setIsInvalidLength(drugIds.length > MAX_DRUG_INPUT);

        // Log the invalid parts
        if (invalidParts.length > 0) {
            setInvalidId(invalidParts.toString());
        } else {
            setInvalidId(null);
        }
    };

    const handleSearchClick = async () => {
        try {
            const response = await getDrugsList(inputDrugs);
            setDrugList(response.data.drugDataList);
        } catch (error) {
            console.error('Error:', error); // Handle errors
        }
    };

    const resetInputDrugList = () => {
        setInputDrugs('');
        setIsInvalidLength(false);
        setInvalidId(null);
        setDrugList([]);
    };

    return { drugList, inputDrugs, setInputDrugs, handleDrugIdsChange, invalidId, isInvalidLength, resetInputDrugList, handleSearchClick };
};

const getStringDate = () => {
    const date = new Date();

    // Format the date as "day-month-year"
    const formattedDate = formatDate(date,DD_MM_YYYY);

    // Format the time as "hour_minute"
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const formattedTime = `${hours}_${minutes}`;

    // Combine everything into the required format
    const resultString = `DrugList-${formattedDate}_${formattedTime}`;
    return resultString;
};


export const exportToCSV = (filteredObject, hiddenColumns) => {
    const displayColumnsValues = manageDrugsColumns
        .map(column => {
            if (!hiddenColumns?.has(column.id)) {
                return column.id;
            }
        })
        .filter(id => id !== undefined);

    // Convert the data to an array of rows, each row corresponds to an object where
    // the displayColumnsValues matches the keys of filteredObject JSON array
    const rows = filteredObject.map(item => {
        return displayColumnsValues.map(columnId => {
            // Safely extract the value for the column, use empty string if not found
            return item[columnId] || '';
        });
    });

    // Add display headers as the first row
    const displayColumnsNames = manageDrugsColumns
        .map(column => {
            if (!hiddenColumns?.has(column.id)) {
                return column.text;
            }
        })
        .filter(id => id !== undefined);
    rows.unshift(displayColumnsNames);

    // Create a worksheet from the data
    const ws = XLSX.utils.aoa_to_sheet(rows);

    // Create a workbook
    const wb = XLSX.utils.book_new();

    // Append the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    // Write the workbook to CSV and trigger download
    XLSX.writeFile(wb, `${getStringDate()}.csv`);

};
